import Facade from '@/Facade'
import GameSignals from '@/signals/GameSignals'
import { ISound } from '@modules/src/interfaces/ISound'
import { ModulesFacade } from '@modules/src/ModulesFacade'
import { DeviceUtils } from '@modules/src/utils/DeviceUtils'
import { Howl, Howler } from 'howler'
import { SoundVolume } from '@/managers/sound/SoundVolume'
import { SoundEvent } from '@modules/src/enums/SoundEvent'
import gsap from 'gsap'
import { RestoreUtil } from '@/utils/RestoreUtil'
import { Model } from '@/model/Model'
import { buildAssetUrl } from '@/utils/AssetsUrlUtil'

export class SoundManager {
    public static model: Model
    public static muteStates: Record<string, boolean>
    public static readonly CROSS_FADE_DURATION: number = 1200
    public static readonly BIG_WIN_FADE_OUT: number = 1000
    public static readonly BIG_WIN_FADE_IN: number = 500
    public static readonly WIN_LAYER_FADE_DURATION: number = 2000
    public static readonly STOP_WIN_LAYER_FADE_DURATION: number = 2000
    public static readonly DURATION_BEFORE_BIG_WIN_END: number = 500
    public static readonly SUSPENSE_STOP_DURATION: number = 500
    public static isBigWinEndSoundPlay: boolean
    public static lpWinSoundIndex: number
    public static hpWinSoundIndex: number
    public static masterVolume: number
    private _soundGroupsPollingId?: number

    public constructor() {
        SoundManager.muteStates = {}
        SoundManager.isBigWinEndSoundPlay = false
        SoundManager.masterVolume = 1
    }

    private clampVolume = (value: number): number => {
        if (!Number.isFinite(value)) {
            return 1
        }

        return Math.max(0, Math.min(1, value))
    }

    private onSetMasterVolume = (value: number) => {
        const volume = this.clampVolume(value)
        SoundManager.masterVolume = volume

        if (Facade.defaultConfig) {
            Facade.defaultConfig.volume = volume
        }

        Howler.volume(volume)
        this.onToggleSoundGroups(volume > 0)
    }

    private syncHowlerMuteFallback = () => {
        const soundsEnabled = SoundManager.muteStates.sounds ?? Facade.defaultConfig?.sound ?? true
        const musicEnabled = SoundManager.muteStates.music ?? Facade.defaultConfig?.music ?? true
        const hasAnyEnabled = !!soundsEnabled || !!musicEnabled

        Howler.mute(!hasAnyEnabled)
    }

    private applyStoredAudioStatesToGroups = () => {
        if (!ModulesFacade.soundGroups) {
            this.syncHowlerMuteFallback()
            return
        }

        const soundsEnabled = SoundManager.muteStates.sounds ?? Facade.defaultConfig?.sound ?? true
        const musicEnabled = SoundManager.muteStates.music ?? Facade.defaultConfig?.music ?? true

        const soundsGroup = ModulesFacade.soundGroups.sounds
        const musicGroup = ModulesFacade.soundGroups.music

        if (soundsGroup) {
            soundsEnabled ? soundsGroup.unMuteGroup() : soundsGroup.muteGroup()
        }

        if (musicGroup) {
            musicEnabled ? musicGroup.unMuteGroup() : musicGroup.muteGroup()
        }

        this.syncHowlerMuteFallback()
    }

    private waitForSoundGroupsInitialization = () => {
        if (this._soundGroupsPollingId) {
            window.clearInterval(this._soundGroupsPollingId)
            this._soundGroupsPollingId = undefined
        }

        this._soundGroupsPollingId = window.setInterval(() => {
            if (!ModulesFacade.soundGroups) {
                return
            }

            this.applyStoredAudioStatesToGroups()

            if (this._soundGroupsPollingId) {
                window.clearInterval(this._soundGroupsPollingId)
                this._soundGroupsPollingId = undefined
            }
        }, 250)
    }

    private onToggleSoundGroup = (group: string, value: boolean) => {
        // Early return if defaultConfig is not loaded yet
        if (!Facade.defaultConfig) {
            console.warn('SoundManager: defaultConfig not yet loaded')
            return
        }

        const soundGroup = ModulesFacade.soundGroups?.[group]
        SoundManager.muteStates[group] = value

        if (group === 'sounds') {
            Facade.defaultConfig.sound = value
        } else {
            Facade.defaultConfig.music = value
        }

        if (soundGroup != null) {
            if (value) {
                soundGroup.unMuteGroup()
            } else {
                soundGroup.muteGroup()
            }
        }

        this.syncHowlerMuteFallback()
    }

    private onToggleSoundGroups = (value: boolean) => {
        this.onToggleSoundGroup('sounds', value)
        this.onToggleSoundGroup('music', value)
    }

    private onVisibilityChange = () => {
        Howler.mute(document.visibilityState === 'hidden')
    }

    private initialize(model: Model): void {
        SoundManager.model = model
        window.addEventListener('visibilitychange', this.onVisibilityChange)

        GameSignals.toggleSoundGroup.connect(this.onToggleSoundGroup)
        GameSignals.setMasterVolume.connect(this.onSetMasterVolume)
        GameSignals.unMute.connect(this.onToggleSoundGroups.bind(this, true))
        GameSignals.mute.connect(this.onToggleSoundGroups.bind(this, false))

        const localStorageData = {
            music: RestoreUtil.getKeyData(RestoreUtil.MUSIC_KEY),
            sound: RestoreUtil.getKeyData(RestoreUtil.SOUND_KEY),
            volume: RestoreUtil.getKeyData(RestoreUtil.VOLUME_KEY),
        }

        const fallbackVolume = typeof Facade.defaultConfig?.volume === 'number' ? Facade.defaultConfig.volume : 1
        const persistedVolume = Number(localStorageData.volume)
        const fallbackMuted = localStorageData.sound === 0 && localStorageData.music === 0
        const initialVolume = this.clampVolume(
            Number.isFinite(persistedVolume) ? persistedVolume : fallbackMuted ? 0 : fallbackVolume
        )

        this.onSetMasterVolume(initialVolume)
        RestoreUtil.setKeyData(RestoreUtil.VOLUME_KEY, `${initialVolume}`)
        RestoreUtil.setKeyData(RestoreUtil.SOUND_KEY, `${+(initialVolume > 0)}`)
        RestoreUtil.setKeyData(RestoreUtil.MUSIC_KEY, `${+(initialVolume > 0)}`)

        this.waitForSoundGroupsInitialization()
    }

    public static createConfigPath(): string {
        const channelType = DeviceUtils.isMobileDevice() ? 'mobile' : 'desktop'
        return buildAssetUrl(`/assets/sounds/sounds_${channelType}.json`)
    }

    public static initializeHowlerConfig(model: Model): void {
        new SoundManager().initialize(model)
        Howler.autoSuspend = false
    }

    public static createSounds(): ISound[] {
        return [
            {
                group: 'sounds',
                name: 'gen_sounds_bet_decreasestd',
                volume: SoundVolume.UI_BUTTONS,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.changeBet],
                        event: SoundEvent.CUSTOM,
                        callback: (settings: ISound, sound: Howl, args: any) => {
                            if (args[0] < this.model.currentBaseBet && args[0] !== +this.model.bets.at(0).toFixed(2)) {
                                sound.play()
                            }
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'gen_sounds_bet_increasestd',
                volume: SoundVolume.UI_BUTTONS,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.changeBet],
                        event: SoundEvent.CUSTOM,
                        callback: (settings: ISound, sound: Howl, args: any) => {
                            if (args[0] > this.model.currentBaseBet && args[0] !== +this.model.bets.at(-1).toFixed(2)) {
                                sound.play()
                            }
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'gen_sounds_bet_increase_decrease_max',
                volume: SoundVolume.UI_BUTTONS,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.changeBet],
                        event: SoundEvent.CUSTOM,
                        callback: (settings: ISound, sound: Howl, args: any) => {
                            if (
                                args[0] === +this.model.bets.at(0).toFixed(2) ||
                                args[0] === +this.model.bets.at(-1).toFixed(2)
                            ) {
                                sound.play()
                            }
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'gen_ui_sound',
                volume: SoundVolume.UI_BUTTONS,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.buttonClicked],
                        event: SoundEvent.CUSTOM,
                        callback: (settings: ISound, sound: Howl, args: any) => {
                            if (
                                args[0] &&
                                args[0] !== 'ui-footer-bet_prev' &&
                                args[0] !== 'ui-footer-bet_next' &&
                                args[0] !== 'ui-game-ctrl-spin'
                            ) {
                                sound.play()
                            }
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.playUIGenSound],
                        event: SoundEvent.PLAY,
                    },
                ],
            },
            {
                group: 'music',
                name: 'main_theme_base_layer_2',
                volume: 0,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.completeSplashScreenState],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            sound.play()
                            sound.loop(true)
                            /**Strange fix, when tapping on the screen during recovery, the music worked with the music button turned off**/
                            if (this.model.isFreeSpinsRestore) {
                                sound.volume(0)
                            } else {
                                sound.fade(0, SoundVolume.MAIN_THEME, SoundManager.CROSS_FADE_DURATION)
                            }
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.startFreeSpinsMusic],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            if (!this.model.isFreeSpinsRestore) {
                                sound.fade(SoundVolume.MAIN_THEME, 0, SoundManager.CROSS_FADE_DURATION / 2)
                            }
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.stopFreeSpinsMusic],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            sound.fade(0, SoundVolume.MAIN_THEME, SoundManager.CROSS_FADE_DURATION)
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.showBigWin],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            if (!this.model.isBonusGameNow) {
                                sound.fade(SoundVolume.MAIN_THEME, 0, SoundManager.BIG_WIN_FADE_IN)
                            }
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.showBigWinComplete],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            if (!this.model.isBonusGameNow) {
                                sound.fade(0, SoundVolume.MAIN_THEME, SoundManager.BIG_WIN_FADE_OUT)
                            }
                        },
                    },
                ],
            },
            {
                group: 'music',
                name: 'main_theme_win_layer_2',
                volume: 0,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.completeSplashScreenState],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            sound.play()
                            sound.loop(true)
                            sound.volume(0)
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.startWinLayerMusic],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            sound.fade(0, SoundVolume.MAIN_WIN_THEME, SoundManager.WIN_LAYER_FADE_DURATION)
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.stopWinLayerMusic],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            if (this.model.isBonusGameNow) {
                                sound.volume(0)
                            } else {
                                sound.fade(SoundVolume.MAIN_WIN_THEME, 0, SoundManager.STOP_WIN_LAYER_FADE_DURATION)
                            }
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.startFreeSpinsMusic],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            sound.volume(0)
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.showBigWin],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            if (!this.model.isBonusGameNow) {
                                sound.fade(SoundVolume.MAIN_WIN_THEME, 0, SoundManager.BIG_WIN_FADE_IN)
                            }
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.showBigWinComplete],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            if (!this.model.isBonusGameNow) {
                                sound.fade(0, SoundVolume.MAIN_WIN_THEME, SoundManager.BIG_WIN_FADE_OUT)
                            }
                        },
                    },
                ],
            },
            {
                group: 'music',
                name: 'freespins_theme_1',
                volume: 0,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.startFreeSpinsMusic],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            sound.play()
                            sound.loop(true)
                            /**Strange fix, when tapping on the screen during recovery, the music worked with the music button turned off**/
                            if (this.model.isFreeSpinsRestore) {
                                sound.volume(SoundVolume.FS_THEME)
                            } else {
                                sound.fade(0, SoundVolume.FS_THEME, SoundManager.CROSS_FADE_DURATION)
                            }
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.stopFreeSpinsMusic],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            sound.fade(SoundVolume.FS_THEME, 0, SoundManager.CROSS_FADE_DURATION)
                            gsap.to(sound, {
                                duration: SoundManager.CROSS_FADE_DURATION,
                                onComplete: () => {
                                    sound.stop()
                                },
                            })
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.showBigWin],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            if (this.model.isBonusGameNow) {
                                sound.fade(SoundVolume.FS_THEME, 0, SoundManager.BIG_WIN_FADE_IN)
                            }
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.showBigWinComplete],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            if (this.model.isBonusGameNow) {
                                sound.fade(0, SoundVolume.FS_THEME, SoundManager.BIG_WIN_FADE_OUT)
                            }
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'symbol_pop1_1',
                volume: SoundVolume.SYMBOL_POP,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.showRegularWin],
                        event: SoundEvent.CUSTOM,
                        callback: (settings: ISound, sound: Howl, args: any) => {
                            if (this.model.cascadeIndex === 0) {
                                gsap.to(this, {
                                    duration: 0.8,
                                    onComplete: () => {
                                        sound.play()
                                    },
                                })
                            }
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'symbol_pop2_1',
                volume: SoundVolume.SYMBOL_POP,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.showRegularWin],
                        event: SoundEvent.CUSTOM,
                        callback: (settings: ISound, sound: Howl, args: any) => {
                            if (this.model.cascadeIndex === 1) {
                                gsap.to(this, {
                                    duration: 0.8,
                                    onComplete: () => {
                                        sound.play()
                                    },
                                })
                            }
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'symbol_pop3_1',
                volume: SoundVolume.SYMBOL_POP,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.showRegularWin],
                        event: SoundEvent.CUSTOM,
                        callback: (settings: ISound, sound: Howl, args: any) => {
                            if (this.model.cascadeIndex === 2) {
                                gsap.to(this, {
                                    duration: 0.8,
                                    onComplete: () => {
                                        sound.play()
                                    },
                                })
                            }
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'symbol_pop4_1',
                volume: SoundVolume.SYMBOL_POP,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.showRegularWin],
                        event: SoundEvent.CUSTOM,
                        callback: (settings: ISound, sound: Howl, args: any) => {
                            if (this.model.cascadeIndex === 3) {
                                gsap.to(this, {
                                    duration: 0.8,
                                    onComplete: () => {
                                        sound.play()
                                    },
                                })
                            }
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'symbol_pop5_1',
                volume: SoundVolume.SYMBOL_POP,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.showRegularWin],
                        event: SoundEvent.CUSTOM,
                        callback: (settings: ISound, sound: Howl, args: any) => {
                            if (this.model.cascadeIndex === 4) {
                                gsap.to(this, {
                                    duration: 0.8,
                                    onComplete: () => {
                                        sound.play()
                                    },
                                })
                            }
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'symbol_pop6_1',
                volume: SoundVolume.SYMBOL_POP,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.showRegularWin],
                        event: SoundEvent.CUSTOM,
                        callback: (settings: ISound, sound: Howl, args: any) => {
                            if (this.model.cascadeIndex > 4) {
                                gsap.to(this, {
                                    duration: 0.8,
                                    onComplete: () => {
                                        sound.play()
                                    },
                                })
                            }
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'wild_win_1',
                volume: SoundVolume.WILD_WIN,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.showRegularWin],
                        event: SoundEvent.CUSTOM,
                        callback: (settings: ISound, sound: Howl, args: any) => {
                            if (this.model.combinationsData.isWild) {
                                sound.play()
                            }
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'hp1_1',
                volume: SoundVolume.HP_WIN,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.showRegularWin],
                        event: SoundEvent.CUSTOM,
                        callback: (settings: ISound, sound: Howl, args: any) => {
                            if (
                                !this.model.combinationsData.isWild &&
                                this.model.combinationsData.isHP &&
                                SoundManager.hpWinSoundIndex === 0
                            ) {
                                sound.play()
                            }
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'hp2_1',
                volume: SoundVolume.HP_WIN,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.showRegularWin],
                        event: SoundEvent.CUSTOM,
                        callback: (settings: ISound, sound: Howl, args: any) => {
                            if (
                                !this.model.combinationsData.isWild &&
                                this.model.combinationsData.isHP &&
                                SoundManager.hpWinSoundIndex === 1
                            ) {
                                sound.play()
                            }
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'hp3_1',
                volume: SoundVolume.HP_WIN,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.showRegularWin],
                        event: SoundEvent.CUSTOM,
                        callback: (settings: ISound, sound: Howl, args: any) => {
                            if (
                                !this.model.combinationsData.isWild &&
                                this.model.combinationsData.isHP &&
                                SoundManager.hpWinSoundIndex === 2
                            ) {
                                sound.play()
                            }
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'lp1_1',
                volume: SoundVolume.LP_WIN,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.showRegularWin],
                        event: SoundEvent.CUSTOM,
                        callback: (settings: ISound, sound: Howl, args: any) => {
                            if (
                                !this.model.combinationsData.isWild &&
                                !this.model.combinationsData.isHP &&
                                this.model.combinationsData.isLP &&
                                SoundManager.lpWinSoundIndex === 0
                            ) {
                                sound.play()
                            }
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'lp2_1',
                volume: SoundVolume.LP_WIN,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.showRegularWin],
                        event: SoundEvent.CUSTOM,
                        callback: (settings: ISound, sound: Howl, args: any) => {
                            if (
                                !this.model.combinationsData.isWild &&
                                !this.model.combinationsData.isHP &&
                                this.model.combinationsData.isLP &&
                                SoundManager.lpWinSoundIndex === 1
                            ) {
                                sound.play()
                            }
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'lp3_1',
                volume: SoundVolume.LP_WIN,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.showRegularWin],
                        event: SoundEvent.CUSTOM,
                        callback: (settings: ISound, sound: Howl, args: any) => {
                            if (
                                !this.model.combinationsData.isWild &&
                                !this.model.combinationsData.isHP &&
                                this.model.combinationsData.isLP &&
                                SoundManager.lpWinSoundIndex === 2
                            ) {
                                sound.play()
                            }
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'scatter_land1_1',
                volume: SoundVolume.SCATTER_LAND,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.scatterLanded],
                        event: SoundEvent.CUSTOM,
                        callback: (settings: ISound, sound: Howl, args: any) => {
                            if (args[0] === 1) {
                                sound.play()
                            }
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'scatter_land2_1',
                volume: SoundVolume.SCATTER_LAND,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.scatterLanded],
                        event: SoundEvent.CUSTOM,
                        callback: (settings: ISound, sound: Howl, args: any) => {
                            if (args[0] === 2) {
                                sound.play()
                            }
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'scatter_land3_1',
                volume: SoundVolume.SCATTER_LAND,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.scatterLanded],
                        event: SoundEvent.CUSTOM,
                        callback: (settings: ISound, sound: Howl, args: any) => {
                            if (args[0] === 3) {
                                sound.play()
                            }
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'scatter_land4_1',
                volume: SoundVolume.SCATTER_LAND,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.scatterLanded],
                        event: SoundEvent.CUSTOM,
                        callback: (settings: ISound, sound: Howl, args: any) => {
                            if (args[0] === 4) {
                                sound.play()
                            }
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'scatter_land5_1',
                volume: SoundVolume.SCATTER_LAND,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.scatterLanded],
                        event: SoundEvent.CUSTOM,
                        callback: (settings: ISound, sound: Howl, args: any) => {
                            if (args[0] === 5) {
                                sound.play()
                            }
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'scatter_land6_1',
                volume: SoundVolume.SCATTER_LAND,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.scatterLanded],
                        event: SoundEvent.CUSTOM,
                        callback: (settings: ISound, sound: Howl, args: any) => {
                            if (args[0] === 6) {
                                sound.play()
                            }
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'scatter_feature_trigger_1',
                volume: SoundVolume.SCATTER_TRIGGER,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.activateScatters],
                        event: SoundEvent.PLAY,
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'intro_1',
                volume: SoundVolume.INTRO_SOUND,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.splashScreenButtonClicked],
                        event: SoundEvent.PLAY,
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'feature_transition_2',
                volume: SoundVolume.INTRO_TRANSITION_SOUND,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.splashScreenButtonClicked],
                        event: SoundEvent.CUSTOM,
                        callback: (settings: ISound, sound: Howl, args: any) => {
                            gsap.to(this, {
                                duration: 2,
                                onComplete: () => {
                                    sound.play()
                                },
                            })
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.bonusStart, GameSignals.bonusStop],
                        event: SoundEvent.PLAY,
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'reel_start_1',
                volume: SoundVolume.REEL_START,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.startSpin],
                        event: SoundEvent.PLAY,
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'reel_spin_1',
                volume: SoundVolume.REEL_SPIN,
                loop: true,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.startSpin],
                        event: SoundEvent.PLAY,
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.errorStop],
                        event: SoundEvent.STOP,
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.reelStopped],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            const index = signalData[0]
                            const isQuick = signalData[1]

                            if (isQuick) {
                                if (index === Facade.reelsConfig.TOTAL_REELS - 1) {
                                    setTimeout(() => {
                                        sound.stop()
                                    }, 100)
                                }
                            } else {
                                setTimeout(() => {
                                    if (index === Facade.reelsConfig.TOTAL_REELS - 1) {
                                        sound.stop()
                                    }
                                }, 100)
                            }
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'reel_stop_1',
                volume: SoundVolume.REEL_STOP,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.stopSpin],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            if (Facade.isTurboSpin) {
                                return
                            }

                            gsap.to(this, {
                                duration: 0.7,
                                onComplete: () => {
                                    sound.play()
                                },
                            })
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.reelsImmediatelyStopped],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            gsap.killTweensOf(this)
                            sound.stop()
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.errorStop],
                        event: SoundEvent.STOP,
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'reel_stop_quick_1',
                volume: SoundVolume.REEL_STOP,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.stopSpin],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            if (Facade.isTurboSpin) {
                                gsap.to(this, {
                                    duration: 0.4,
                                    onComplete: () => {
                                        sound.play()
                                    },
                                })
                            }
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.reelsImmediatelyStopped],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            gsap.killTweensOf(this)
                            gsap.to(this, {
                                duration: 0.1,
                                onComplete: () => {
                                    sound.play()
                                },
                            })
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.errorStop],
                        event: SoundEvent.STOP,
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'big_win_trigger_1',
                volume: SoundVolume.BIG_WIN_TRIGGER,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.showBigWin, GameSignals.startBigWinLevel],
                        event: SoundEvent.PLAY,
                    },
                ],
            },
            {
                group: 'music',
                name: 'big_win_theme_1',
                volume: 0,
                loop: true,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.showBigWin],
                        event: SoundEvent.PLAY,
                        eventData: {
                            volume: SoundVolume.BIG_WIN_THEME,
                            duration: SoundManager.CROSS_FADE_DURATION,
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.playBigWinEndSound, GameSignals.skipBigWin],
                        event: SoundEvent.STOP,
                        eventData: {
                            volume: 0,
                            duration: SoundManager.DURATION_BEFORE_BIG_WIN_END,
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'big_win_end_1',
                volume: SoundVolume.BIG_WIN_END,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.playBigWinEndSound],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings: ISound, sound: Howl, args: any) => {
                            if (SoundManager.isBigWinEndSoundPlay) return
                            sound.play()
                            sound.fade(0, SoundVolume.BIG_WIN_END, SoundManager.DURATION_BEFORE_BIG_WIN_END)
                            SoundManager.isBigWinEndSoundPlay = true
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.bigWinSoundsComplete],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings: ISound, sound: Howl, args: any) => {
                            SoundManager.isBigWinEndSoundPlay = false
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'gen_big_win_count_up_1',
                volume: SoundVolume.BIG_WIN_COUNT_UP,
                loop: true,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.showBigWin],
                        event: SoundEvent.PLAY,
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.playBigWinEndSound],
                        event: SoundEvent.STOP,
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'gen_big_win_count_up_tail_1',
                volume: SoundVolume.BIG_WIN_COUNT_UP,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.playBigWinEndSound],
                        event: SoundEvent.PLAY,
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'feature_panel_appear_1',
                volume: SoundVolume.APPEAR_POPUP,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.showFreeSpinsIntro],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings: ISound, sound: Howl, args: any) => {
                            gsap.to(this, {
                                duration: 0.5,
                                onComplete: () => {
                                    sound.play()
                                },
                            })
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.showFreeSpinsRetrigger],
                        event: SoundEvent.PLAY,
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.showFreeSpinsSummary],
                        event: SoundEvent.PLAY,
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'feature_panel_disappear_1',
                volume: SoundVolume.DISAPPEAR_POPUP,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.hidePopup],
                        event: SoundEvent.PLAY,
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'multiplier_land_1',
                volume: SoundVolume.MULTIPLIER_LAND,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.playMultiplierLandSound],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings: ISound, sound: Howl, args: any) => {
                            args[0] < 75 ? sound.volume(0.8) : sound.volume(1)

                            sound.play()
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'multiplier_collect_1',
                volume: SoundVolume.MULTIPLIER_COLLECT,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.showMultiplierLandAnimation],
                        event: SoundEvent.PLAY,
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.playMultiplierTrailAnimation],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings: ISound, sound: Howl, args: any) => {
                            gsap.to(this, {
                                duration: 0.8,
                                onComplete: () => {
                                    sound.play()
                                },
                            })
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'multiplier_jar_pop_1',
                volume: SoundVolume.MULTIPLIER_JAR,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.multiplierLanded],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings: ISound, sound: Howl, args: any) => {
                            if (!args[1]) {
                                sound.play()
                            }
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'multiplier_fly_to_win_panel_1',
                volume: SoundVolume.MULTIPLIER_TRAIL,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.playMultiplierTrailAnimation],
                        event: SoundEvent.PLAY,
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'symbol_drop_1',
                volume: SoundVolume.SYMBOLS_DROP,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.symbolsDrop],
                        event: SoundEvent.CUSTOM,
                        callback: (settings: ISound, sound: Howl, args: any) => {
                            gsap.to(this, {
                                duration: 0.1,
                                onComplete: () => {
                                    sound.play()
                                },
                            })
                        },
                    },
                ],
            },
        ]
    }
}
